import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Paper,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MagazineCoverUploader from '../../MagazineCoverUploader';
import MagazineFieldEditor from './MagazineFieldEditor';
import MagazinePreviousIssue from './MagazinePreviousIssue';
import { useSuperDuper } from '../../context/SuperDuperContext';

/**
 * Collapsible manager for one magazine level (beginner / junior / senior):
 * the current issue link + cover, plus the list of previous issues.
 */
const MagazineLevelSection = ({ config }) => {
  const {
    magazines,
    magazinesLoading,
    expandedLevel,
    setExpandedLevel,
    editingField,
    tempValue,
    setTempValue,
    handleEditField,
    handleSaveField,
    handleCancelMagazineEdit,
    handleClearField,
    handleCoverUploadSuccess,
    handleAddPreviousIssue,
    handleCopyToClipboard
  } = useSuperDuper();

  const { level, title } = config;
  const levelData = magazines[level];
  const isExpanded = expandedLevel === level;
  const previousIssues = levelData.previous || [];

  return (
    <Card sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${config.cardBorderColor}` }}>
      <CardContent>
        <Box
          onClick={() => setExpandedLevel(isExpanded ? null : level)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            cursor: 'pointer',
            mb: 2,
            pb: 2,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Typography variant="h6" sx={{ color: '#c64110', fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {title}
          </Typography>
          <Button size="small" endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ color: '#c64110' }}>
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {/* Current Issue */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#c64110', fontWeight: 600, mb: 2 }}>
              Current Issue
            </Typography>

            {/* Current Link */}
            <MagazineFieldEditor
              label="Current Issue Link"
              labelColor="#c64110"
              isEditing={
                editingField?.level === level &&
                editingField?.section === 'current' &&
                editingField?.field === 'link'
              }
              value={levelData.current.link}
              tempValue={tempValue}
              setTempValue={setTempValue}
              rows={3}
              inputSx={config.currentLinkInputSx}
              displayBg={config.currentLinkDisplayBg}
              displayMinHeight="60px"
              disabled={magazinesLoading}
              onSave={handleSaveField}
              onCancel={handleCancelMagazineEdit}
              onEdit={() => handleEditField(level, 'current', 'link')}
              onCopy={() => handleCopyToClipboard(levelData.current.link)}
              onClear={() => handleClearField(level, 'current', 'link')}
            />

            {/* Current Cover */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <MagazineCoverUploader
                label="Current Issue Cover"
                suggestedFilename={`thisweek_${level}.jpeg`}
                currentUrl={levelData.current.cover}
                onUploadSuccess={(url) => handleCoverUploadSuccess(level, 'current', 'cover', null, url)}
              />
            </Paper>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* Previous Issues */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: config.previousHeadingColor, fontWeight: 600 }}>
                Previous Issues
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddPreviousIssue(level)}
                disabled={magazinesLoading}
                sx={{
                  background: 'linear-gradient(45deg, #FF6B35 30%, #FFB74D 70%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF8C35 30%, #FFC74D 70%)',
                  }
                }}
              >
                Add Previous Saturday
              </Button>
            </Box>

            {/* Newest issue first, but mutations still use the real array index */}
            {previousIssues.map((_, displayIdx) => {
              const actualIdx = previousIssues.length - 1 - displayIdx;
              return (
                <MagazinePreviousIssue
                  key={actualIdx}
                  config={config}
                  issue={previousIssues[actualIdx]}
                  actualIdx={actualIdx}
                  previousLength={previousIssues.length}
                />
              );
            })}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MagazineLevelSection;
